import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { useAppSelector, useAppDispatch } from 'app/hooks'
import { LoaderOverlay } from 'components/Loader'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import { HOST_POLL_TIMEOUT } from 'features/home/consts'
import { useIsTabActive } from 'app/hooks'
import { readRmsHomesHome } from '@racepointenergy/swc/scs/read-rms-homes-home'
import { HomeRoutes } from './components'
import { homeAsideRoutes } from './routes'
import { useHomeRouteParams } from './hooks'
import {
  fetchHomeThunk,
  getPartnersInboundThunk,
  getPartnersThunk,
  partnersActions,
  partnersInboundActions,
  selectedHomeActions,
} from './slice'
import Aside from 'features/aside'
import Breadcrumbs from 'components/Breadcrumbs'
import {
  homeJobSelector,
  selectedHomeSelector,
  homeSoftwareVersionSelector,
} from './selectors'
import {
  StyledInnerContainerNearMenu,
  StyledOuterContainer,
} from 'components/Layout/CommonLayout/styles'
import { getPaymentMethodsListThunk } from 'features/paymentMethods/slice'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

import { HomeStateContext } from './homeStateContext'
import { homeConfigActions } from './components/HomeConfiguration/slice'
import { fetchSubscriptionsThunk } from './components/HomeSubscription/slice'
import {
  fetchHomeJobThunk,
  homeJobActions,
} from './components/HomeJobInfo/slice'
import { checkVersion } from './utils'
import { fetchHomeActiveConfigEnergyThunk } from './components/HomeEnergySystem/slice'

function Home() {
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()
  const { path, url } = useRouteMatch()
  const home = useAppSelector(selectedHomeSelector.data)
  const isHomeLoaded = useAppSelector(selectedHomeSelector.isFinished)
  const isHomeJobLoaded = useAppSelector(homeJobSelector.isFinished)
  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const { softwareVersionLabel } = useAppSelector(homeSoftwareVersionSelector)

  const [showPDU, setShowPDU] = useState(false)
  const [showPanels, setShowPanels] = useState(false)

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  useEffect(() => {
    if (homeId) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      setShowPanels(true)
    }
    const e =
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels.filter(
        (panel) => panel.panelType === 'Savant Power Distribution Unit'
      )
    if (e && e.length) {
      setShowPDU(true)
    } else if (isHomeActiveConfigEnergyLoaded) {
      setShowPDU(false)
    }
  }, [
    setShowPDU,
    setShowPanels,
    homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels,
    homeId,
    isHomeActiveConfigEnergyLoaded,
  ])

  const buildHomeMenuLinkPath = useCallback(
    (path: string) => `${url}/${path}`,
    [url]
  )

  const buildHomeRoutePath = useCallback(
    (routePath: string) => `${path}/${routePath}`,
    [path]
  )

  const homeMenu = useMemo(
    () =>
      homeAsideRoutes(
        checkVersion(softwareVersionLabel || ''),
        showPDU,
        showPanels
      ).map(({ title, path, icon }) => ({
        icon,
        title,
        to: buildHomeMenuLinkPath(path),
      })),
    [buildHomeMenuLinkPath, softwareVersionLabel, showPDU, showPanels]
  )

  const homeRoutes = useMemo(
    () =>
      homeAsideRoutes(
        checkVersion(softwareVersionLabel || ''),
        showPDU,
        showPanels
      ).map(({ component, path }) => ({
        component,
        path: buildHomeRoutePath(path),
      })),
    [buildHomeRoutePath, softwareVersionLabel, showPDU, showPanels]
  )

  const defaultRoute = buildHomeMenuLinkPath(homeAsideRoutes()[0].path)

  useEffect(() => {
    async function initHomeInfo() {
      if (homeId) {
        await dispatch(fetchHomeThunk(homeId))
        dispatch(getPartnersThunk(homeId))
        dispatch(getPartnersInboundThunk(homeId))
      }
    }

    initHomeInfo()

    return () => {
      dispatch(selectedHomeActions.clear())
      dispatch(partnersActions.clear())
      dispatch(partnersInboundActions.clear())
    }
  }, [dispatch, homeId])

  useEffect(() => {
    homeId && dispatch(fetchSubscriptionsThunk(homeId))
    rootDomainId && dispatch(getPaymentMethodsListThunk(rootDomainId))
  }, [dispatch, homeId, rootDomainId])

  useEffect(() => {
    if (isHomeLoaded && home) {
      dispatch(fetchHomeJobThunk(home.domainId, { configs: true }))
    }
  }, [dispatch, home, isHomeLoaded])

  useEffect(() => {
    return () => {
      dispatch(homeConfigActions.clear())
      dispatch(homeJobActions.clear())
    }
  }, [dispatch])

  const [isTabActive] = useIsTabActive()
  const prevIsTabActive = useRef<boolean>(isTabActive)
  const {
    data: homeState,
    isLoading: isHomeStateLoading,
    refetch: refetchHomeStatus,
  } = readRmsHomesHome.useQuery(
    { homeId },
    {
      skip: !home || !isTabActive,
      pollingInterval: HOST_POLL_TIMEOUT,
    }
  )

  useEffect(() => {
    if (homeState) {
      dispatch(selectedHomeActions.succeed(homeState))
    }
  }, [homeState, dispatch])

  useEffect(() => {
    if (prevIsTabActive.current !== isTabActive && isTabActive) {
      refetchHomeStatus()
    }
    prevIsTabActive.current = isTabActive
  }, [refetchHomeStatus, isTabActive])

  if (!isHomeLoaded || !isHomeJobLoaded) {
    return <LoaderOverlay isFullScreen />
  }

  if (isHomeLoaded && isHomeJobLoaded && !home) {
    // TODO: add view
    return <div>Home Not Found</div>
  }

  return (
    <StyledOuterContainer>
      <Aside menu={homeMenu} />

      <StyledInnerContainerNearMenu>
        <Breadcrumbs />
        <HomeStateContext.Provider
          value={{
            isOnline: homeState?.online,
            hasMonitorAccessEnabled: homeState?.monitorAccessEnabled,
            isLoading: isHomeStateLoading,
          }}
        >
          <HomeRoutes homeRoutes={homeRoutes} defaultRoute={defaultRoute} />
        </HomeStateContext.Provider>
      </StyledInnerContainerNearMenu>
    </StyledOuterContainer>
  )
}

export default Home
